import { ChipOption } from '@marageti/z4-lib';
import {
  TicketSubType,
  TicketStatus,
  TicketType,
  Assignee,
} from '@marageti/z4-sdk/lib/tickets';
import {
  ANY_ASSIGNEE,
  teamMemberSearchRequest,
  formatTeamMembersAsOptions,
} from '../../utils/team-members';
import type { Option, AssigneeOption } from '../../utils/team-members';

export type { Option, AssigneeOption };
export { ANY_ASSIGNEE, teamMemberSearchRequest, formatTeamMembersAsOptions };

// #region Filter & sort options

export const ALL_TYPE_FILTER: Option = { title: 'All', value: '' };

export const TYPE_FILTER_OPTIONS: Option[] = [
  { title: 'Traveler Issue', value: 'TRAVELER_ISSUE' },
  { title: 'Traveler Behavior', value: 'TRAVELER_BEHAVIOR' },
  { title: 'Traveler Review', value: 'TRAVELER_REVIEW' },
  { title: 'Traveler Requested Offline', value: 'TRAVELER_REQUESTED_OFFLINE' },
  { title: 'Partner Operations (Trip Experience)', value: 'PARTNER_OPERATIONS' },
  { title: 'Partner Behavior', value: 'PARTNER_BEHAVIOR' },
  { title: 'Partner Requested Offline', value: 'PARTNER_REQUESTED_OFFLINE' },
  { title: 'Unreported Sale', value: 'UNREPORTED_SALE' },
  { title: 'Trip Cancellation', value: 'TRIP_CANCELLATION' },
  { title: 'Other (Last Resort)', value: 'OTHER' },
];

export const STATUS_CHIPS: ChipOption[] = [
  { key: 'PENDING', label: 'Open', value: 'PENDING' },
  { key: 'WAITING_ON_TRAVELER_CARE', label: 'Waiting on Traveler Care', value: 'WAITING_ON_TRAVELER_CARE' },
  { key: 'WAITING_ON_PARTNERSHIP', label: 'Waiting on Partnership', value: 'WAITING_ON_PARTNERSHIP' },
  { key: 'CLOSED', label: 'Closed', value: 'CLOSED' },
];

export const STATUS_DISPLAY_LABELS: Record<TicketStatus, string> = {
  PENDING: 'Open',
  WAITING_ON_TRAVELER_CARE: 'Waiting on Traveler Care',
  WAITING_ON_PARTNERSHIP: 'Waiting on Partnership',
  COMPLETED: 'Closed',
  CANCELED: 'Dismissed',
};

export const STATUS_MENU_OPTIONS: { label: string; value: TicketStatus }[] = [
  { label: 'Open', value: 'PENDING' },
  { label: 'Waiting on Traveler Care', value: 'WAITING_ON_TRAVELER_CARE' },
  { label: 'Waiting on Partnership', value: 'WAITING_ON_PARTNERSHIP' },
  { label: 'Closed', value: 'COMPLETED' },
];

export const SUBTYPE_DISPLAY_LABELS: Record<TicketSubType, string> = {
  FIRST_FOLLOW_UP: 'First Follow-up',
  '45_DAY_FOLLOW_UP': '45 Day Follow-up',
  '90_DAY_FOLLOW_UP': '90 Day Follow-up',
  SOLD_TRIP_FOLLOW_UP: 'Sold Trip Follow-up',
  WELL_WISH_FOLLOW_UP: 'Well Wish Follow-up',
  TRAVELER_ISSUE: 'Traveler Issue',
  TRAVELER_BEHAVIOR: 'Traveler Behavior',
  TRAVELER_REQUESTED_OFFLINE: 'Traveler Requested Offline',
  TRAVELER_REVIEW: 'Traveler Review',
  PARTNER_OPERATIONS: 'Partner Operations (Trip Experience)',
  PARTNER_BEHAVIOR: 'Partner Behavior',
  PARTNER_REQUESTED_OFFLINE: 'Partner Requested Offline',
  UNREPORTED_SALE: 'Unreported Sale',
  TRIP_CANCELLATION: 'Trip Cancellation',
  OTHER: 'Other (Last Resort)',
};

export const SEVERITY_CHIPS: ChipOption[] = [
  { key: 'NO_ISSUE', label: 'No Issue', value: 'NO_ISSUE' },
  { key: 'MINOR', label: 'Minor Issue', value: 'MINOR' },
  { key: 'MAJOR', label: 'Major Issue', value: 'MAJOR' },
  { key: 'CRITICAL', label: 'Critical Issue', value: 'CRITICAL' },
];

export const SORT_OPTIONS: Option[] = [
  { title: 'Date Created (Newest)', value: 'createdAt:desc' },
  { title: 'Date Created (Oldest)', value: 'createdAt:asc' },
  { title: 'Date Updated (Newest)', value: 'updatedAt:desc' },
  { title: 'Date Updated (Oldest)', value: 'updatedAt:asc' },
];

export interface TicketsDefaultSearch {
  [key: string]: string | string[];
  search: string;
  type: string[];
  assignee: string;
  sort: string;
  status: string[];
  severity: string[];
}

export const defaultSearch: TicketsDefaultSearch = {
  search: '',
  type: [],
  assignee: '',
  sort: 'createdAt:desc',
  status: [],
  severity: [],
};

// #endregion

// #region Ticket form types

export type TicketTypeOption = {
  label: string;
  value: TicketSubType;
  ticketType: TicketType;
};

export type TicketStatusOption = {
  label: string;
  value: TicketStatus;
};

export type TripRequestOption = {
  label: string;
  value: string;
};

export type TripMatchOption = {
  label: string;
  value: string;
};

export type TravelerOption = {
  id: string;
  fullName: string;
  email: string;
} | null;

export type TicketFormData = {
  type: TicketTypeOption | null;
  status: TicketStatusOption;
  title: string;
  description: string;
  traveler: TravelerOption;
  tripRequest?: TripRequestOption;
  tripMatch?: TripMatchOption | null;
  assignees: AssigneeOption[];
};

export type EditableTicket = {
  id: string;
  ticketType: TicketType;
  ticketSubType: TicketSubType;
  status: TicketStatus;
  title: string;
  description: string;
  travelerId?: string;
  travelerName?: string;
  travelerEmail?: string;
  tripRequestId?: string;
  tripMatchId?: string;
  assignees: Assignee[];
};

// #endregion
